import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiRoutes } from '@lsq-frontend/angular-shared/app-constants';
import { SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { ISettings } from '@lsq-frontend/angular-shared/settings';
import {
    GetApiTokensRequest,
    GetApiTokensResponse,
    GetLogsRequest,
    GetLogsResponse,
    HealthResponse
} from '@lsq-frontend/remote-services/enterprise/common';
import { ApiService, ErrorResponse } from '@lsq-frontend/angular-shared/api-service';
import { BehaviorSubject } from 'rxjs';

// Some SingletonService could be instantiated multiple times because InjectTokens could be different when
// 1. referenced from federated modules and libraries that are not marked as shared.
// 2. referenced from different import pathes (relative path and absolute path to the same folder are not considered identical).
// This guard can be registered to monitor the singleton instances by the string of class name only.
// Example: SingletonServiceGuard.register(this.constructor.name);
@Injectable({ providedIn: 'root' })
export class SingletonServiceGuard {
    public static readonly classMap = new Set<string>();

    public static register(className: string): boolean {
        if (SingletonServiceGuard.classMap.has(className)) {
            console.debug(
                `[SingletonServiceGuard] ${className} has already been instantiated. Please check SingletonService guidance from Readme.md.`
            );
            return false;
        }
        SingletonServiceGuard.classMap.add(className);
        //console.debug(`[SingletonServiceGuard] ${className} is instantiated.`);
        return true;
    }
}

@Injectable({ providedIn: 'root' })
export class GenericGetApiTokensService extends ApiService<
    GetApiTokensRequest,
    GetApiTokensResponse
> {
    public requestType = GetApiTokensRequest;
    public responseType = GetApiTokensResponse;
    public endpointName = ApiRoutes.GetLogs;

    constructor(http: HttpClient, @Inject(SETTINGS) settings: ISettings) {
        super(http, settings);
        SingletonServiceGuard.register('GenericGetApiTokensService');
    }
}

@Injectable({ providedIn: 'root' })
export class GenericGetLogsService extends ApiService<GetLogsRequest, GetLogsResponse> {
    public requestType = GetLogsRequest;
    public responseType = GetLogsResponse;
    public endpointName = ApiRoutes.GetLogs;

    constructor(http: HttpClient, @Inject(SETTINGS) settings: ISettings) {
        super(http, settings);
        SingletonServiceGuard.register('GenericGetLogsService');
    }
}

@Injectable({ providedIn: 'root' })
export class GenericHealthService extends ApiService<void, HealthResponse> {
    public requestType = undefined;
    public responseType = HealthResponse;
    public endpointName = ApiRoutes.Health;
    public isWaitingForHealthAndUser$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
        true
    );
    public isApiHealthy$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public isExecuting = false;
    constructor(http: HttpClient, @Inject(SETTINGS) settings: ISettings) {
        super(http, settings);
        SingletonServiceGuard.register('GenericHealthService');
    }
    public execute(): void {
        this.isExecuting = true;
        this._http
            .get(this.endpointUrl)
            .subscribe(
                (data: HealthResponse) => this.dataSubject.next(data),
                (error: ErrorResponse) => this.errorSubject.next(error)
            )
            .add(() => {
                this.isExecuting = false;
            });
    }
    public executeOnce(): Promise<HealthResponse> {
        this.isExecuting = true;
        return this._http
            .get<HealthResponse>(this.endpointUrl)
            .toPromise()
            .finally(() => {
                this.isExecuting = false;
            });
    }
}
